// ─────────────────────────────────────────────
//  Overview — main dashboard home page
// ─────────────────────────────────────────────
import QRCode from "../components/QRCode";
import Stars from "../components/Stars";
import { PLATFORMS, SAMPLE_REVIEWS } from "../lib/data";

export default function Overview({ business, setNav, onPreviewCustomer }) {
  const qrVal = `jiffyreview.com/r/${business.name.toLowerCase().replace(/\s+/g, "-")}`;
  const flaggedReview = SAMPLE_REVIEWS.find((r) => r.flagged);

  return (
    <div>
      {/* Greeting */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, letterSpacing: "-0.03em" }}>
          Good morning 👋
        </h1>
        <p style={{ margin: "5px 0 0", color: "rgba(255,255,255,0.4)", fontSize: 13 }}>
          {business.name} · {business.location}
        </p>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Avg Rating",    val: "4.7★", sub: "↑ 0.2 this month",  color: "#FFBE0B" },
          { label: "Total Reviews", val: "143",  sub: "12 this week",       color: "#4ECDC4" },
          { label: "QR Scans",      val: "89",   sub: "Last 30 days",       color: "#FF6B9D" },
          { label: "Response Rate", val: "94%",  sub: "Platforms synced",   color: "#A8DAFF" },
        ].map((s, i) => (
          <div
            key={i}
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: 14,
              padding: "18px 16px",
            }}
          >
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginBottom: 6, fontWeight: 600, letterSpacing: "0.08em" }}>
              {s.label.toUpperCase()}
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: s.color, lineHeight: 1, marginBottom: 5 }}>
              {s.val}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Low-star alert */}
      {flaggedReview && (
        <div
          style={{
            background: "rgba(211,35,35,0.1)",
            border: "1px solid rgba(211,35,35,0.3)",
            borderRadius: 12,
            padding: "13px 16px",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 20 }}>🔔</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#ff6b6b" }}>
              Low-star review alert
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
              {flaggedReview.name} left a {flaggedReview.stars}★ review — privately captured, not yet public.
            </div>
          </div>
          <button
            onClick={() => setNav("reviews")}
            style={{
              padding: "7px 14px", borderRadius: 8,
              border: "1px solid rgba(211,35,35,0.4)",
              background: "rgba(211,35,35,0.15)",
              color: "#ff6b6b", fontSize: 12, fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            View →
          </button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20 }}>
        {/* Recent reviews */}
        <div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginBottom: 12, letterSpacing: "0.1em" }}>
            RECENT REVIEWS
          </div>
          {SAMPLE_REVIEWS.slice(0, 3).map((r) => {
            const p = PLATFORMS.find((x) => x.id === r.platform);
            return (
              <div
                key={r.id}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${r.flagged ? "rgba(211,35,35,0.3)" : "rgba(255,255,255,0.07)"}`,
                  borderRadius: 12, padding: "14px 16px", marginBottom: 10,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                      style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: `${p.color}22`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: p.color, fontWeight: 800, fontSize: 13,
                      }}
                    >
                      {r.name[0]}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{r.name}</div>
                      <Stars value={r.stars} size={12} />
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: p.color, fontWeight: 700 }}>{p.name}</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 2 }}>{r.time}</div>
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.6 }}>{r.text}</div>
                {r.tags.length > 0 && (
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
                    {r.tags.map((t) => (
                      <span key={t} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 10, background: "rgba(255,190,11,0.12)", color: "#FFBE0B" }}>
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* QR card */}
        <div
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 16, padding: 22, textAlign: "center",
          }}
        >
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginBottom: 14, letterSpacing: "0.1em" }}>
            YOUR QR CODE
          </div>
          <div
            style={{
              display: "inline-block", padding: 12, background: "white",
              borderRadius: 14, marginBottom: 14,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
          >
            <QRCode value={qrVal} size={130} />
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginBottom: 16, lineHeight: 1.6 }}>
            Print on receipts or table tents. Customers scan to review in 20 seconds.
          </div>
          <button
            onClick={onPreviewCustomer}
            style={{
              width: "100%", padding: 12, borderRadius: 10, border: "none",
              background: "linear-gradient(135deg,#FF6B35,#FFBE0B)",
              color: "white", fontSize: 13, fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit", marginBottom: 8,
            }}
          >
            Preview Customer Flow →
          </button>
          <button
            onClick={() => setNav("qr")}
            style={{
              width: "100%", padding: 11, borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.1)",
              background: "transparent", color: "rgba(255,255,255,0.4)",
              fontSize: 12, cursor: "pointer", fontFamily: "inherit",
            }}
          >
            Print Templates →
          </button>
        </div>
      </div>
    </div>
  );
}
